/**
 * Notifications de l'application.
 *
 * Une seule porte d'entrée pour les grandes cartes qui glissent depuis la
 * droite : `notify()` et ses raccourcis. Le rendu s'appuie sur vue3-toastify,
 * déjà utilisé partout ailleurs, pour que ces cartes s'empilent avec les
 * toasts existants au lieu de se superposer à eux.
 */
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import i18n from "@/i18n";
import NotificationCard from "@/views/components/NotificationCard.vue";

/* Animation d'entrée et de sortie, définies dans NotificationCard.vue. */
const TRANSITION = {
  enter: "sf-note-enter",
  exit: "sf-note-exit",
  collapse: true,
  collapseDuration: 300,
};

const DEFAULT_TIMEOUT = 5000;

/* Ton de la carte → type vue3-toastify, qui colore la barre de progression. */
const TONE_TO_TYPE = {
  success: "success",
  error: "error",
  warning: "warning",
  info: "info",
};

/**
 * Traduit ce qui est une clé i18n, laisse passer le reste.
 *
 * Les appelants transmettent tantôt une clé (`network.offline_title`), tantôt
 * une phrase déjà rédigée — un message venu du serveur, par exemple. Tester la
 * clé évite d'afficher « network.offline_title » quand elle manque, et de
 * mutiler un texte qui n'en était pas une.
 */
function translate(value, params) {
  if (value === null || value === undefined || value === "") return "";
  const text = String(value);
  return i18n.global.te(text) ? i18n.global.t(text, params || {}) : text;
}

/**
 * Affiche une carte de notification.
 *
 * @param {object}  options
 * @param {string}  options.tone    success | error | warning | info
 * @param {string}  options.title   titre — clé i18n ou texte
 * @param {string}  options.text    message — clé i18n ou texte
 * @param {string}  options.hint    précision discrète, en bas de carte
 * @param {string}  options.icon    classe Bootstrap Icons, sinon celle du ton
 * @param {string}  options.id      identité de la carte : une carte portant
 *                                  déjà cet identifiant est mise à jour au
 *                                  lieu d'être doublée
 * @param {number}  options.timeout durée en ms ; 0 pour une carte qui reste
 *                                  jusqu'à ce qu'on la ferme
 * @returns {string|number|undefined} l'identifiant de la carte
 */
export function notify(options) {
  const opts = options || {};
  const tone = opts.tone || "info";
  const timeout = opts.timeout === undefined ? DEFAULT_TIMEOUT : opts.timeout;
  const sticky = timeout === 0;

  const data = {
    tone,
    icon: opts.icon || "",
    title: translate(opts.title, opts.params),
    text: translate(opts.text, opts.params),
    hint: translate(opts.hint, opts.params),
    /*
     * La carte est rendue dans l'instance Vue de vue3-toastify, où aucun
     * plugin de l'application n'est installé : `$t` n'y existe pas. Tout ce
     * qui doit être traduit l'est donc ici, y compris le libellé du bouton
     * de fermeture.
     */
    closeLabel: translate("button.close"),
  };

  const config = {
    data,
    type: TONE_TO_TYPE[tone] || "default",
    autoClose: sticky ? false : timeout,
    hideProgressBar: sticky,
    position: toast.POSITION.BOTTOM_RIGHT,
    transition: TRANSITION,
    toastClassName: "sf-note-toast",
    /* La carte porte son propre bouton et sa propre icône. */
    closeButton: false,
    icon: false,
    closeOnClick: false,
    pauseOnHover: true,
  };

  if (opts.id) {
    if (toast.isActive(opts.id)) {
      /*
       * Même carte déjà à l'écran : on la remet à jour. C'est ce qui évite
       * dix cartes « pas de connexion » quand dix requêtes échouent d'affilée.
       */
      toast.update(opts.id, { ...config, render: NotificationCard });
      return opts.id;
    }
    toast(NotificationCard, { ...config, toastId: opts.id });
    return opts.id;
  }

  return toast(NotificationCard, config);
}

export function notifySuccess(title, text, options) {
  return notify({ ...(options || {}), tone: "success", title, text });
}

export function notifyError(title, text, options) {
  return notify({ ...(options || {}), tone: "error", title, text });
}

export function notifyWarning(title, text, options) {
  return notify({ ...(options || {}), tone: "warning", title, text });
}

export function notifyInfo(title, text, options) {
  return notify({ ...(options || {}), tone: "info", title, text });
}

/** Retire une carte affichée avec un identifiant. */
export function dismissNotification(id) {
  if (id && toast.isActive(id)) toast.remove(id);
}
